import { Box, Chip, Stack, useMediaQuery } from '@mui/material';
import { NumericFormat } from 'react-number-format';
import { IHeadCell } from '../../../shared/interfaces/table-display.model';
import React, { FunctionComponent, SetStateAction, useContext, useEffect, useState } from 'react';
import { TableDisplay } from '../miscellaneous-components/table-display';
import { useLocation, useNavigate } from 'react-router-dom';
import { IReductionActionForReportPeriod } from '../../../shared/services/reduction-action/reduction-action.model';
import { ReductionActionPlanDialog } from '../../dialogs/reduction/reduction-action-plan-dialog';
import ReductionActionService from '../../../shared/services/reduction-action/reduction-action.service';
import moment from 'moment';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import gaiaTheme from '../../../root-components/theme';
import { TableActionButtons } from '../miscellaneous-components/table-action-buttons';
import { useIsReportPeriodLocked } from '../../../hooks/use-is-report-period-locked';
import { ReductionActionType } from '../../../shared/services/reduction-action/reduction-action.model';
import { costPerTonnePerYear, formatCostPerTonne } from '../../../shared/utils/reduction-utils';

export const ReductionActionPlan: FunctionComponent<{
  actionType: ReductionActionType,
  reductionActions: IReductionActionForReportPeriod[],
  getAllActionsForOrganisation: () => void,
  inputPage: number,
  setInputPage: React.Dispatch<SetStateAction<any>>,
  totalRecords: number,
  perPage: number,
  setPerPage: React.Dispatch<SetStateAction<any>>,
  setOrder: React.Dispatch<SetStateAction<any>>,
  setOrderBy: React.Dispatch<SetStateAction<any>>,
}> = ({
  actionType,
  reductionActions,
  setOrder,
  setOrderBy,
  getAllActionsForOrganisation,
  inputPage,
  setInputPage,
  totalRecords,
  perPage,
  setPerPage,
}) => {

    const { state } = useLocation();
    const navigate = useNavigate()
    //Services
    const reductionActionService = new ReductionActionService()
    const userNotificationsContext = useContext(UserNotificationsContext)
    //Hooks
    const isReportPeriodLocked = useIsReportPeriodLocked()
    //State
    const [selected, setSelected] = useState<string[]>([]);
    const [openDialog, setDialogOpen] = React.useState(false);
    //Loading State
    const [isDialogSubmitting, setIsDialogSubmitting] = useState<boolean>(false)
    //Layout
    const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('sm'));

    const headCells: IHeadCell[] = [
      { id: 'actions', numeric: false, label: 'Action', sortable: false, isHeading: false },
      { id: 'name', numeric: false, label: actionType === 'flag_removal' ? 'Removal Name' : 'Reduction Action', sortable: false, isHeading: true },
      ...(actionType === 'flag_removal'
        ? [{ id: 'removalType', numeric: false, label: 'Removal Type', sortable: false, includeInMobile: false } as IHeadCell]
        : []),
      { id: 'reportingPeriod', numeric: false, label: 'Reporting Period', sortable: false, includeInMobile: false },
      { id: 'projectedAnnualReduction', numeric: true, label: actionType === 'flag_removal' ? 'Projected Annual Removal' : 'Projected Annual Reduction', sortable: false, includeInMobile: false },
      ...(actionType === 'flag_removal'
        ? []
        : [{ id: 'costPerTonne', numeric: true, label: 'Cost per tCO2e', sortable: false, includeInMobile: false } as IHeadCell]),
      { id: 'startDate', numeric: false, label: 'Date Added', sortable: false, includeInMobile: false },
      { id: 'endDate', numeric: false, label: 'Due Date', sortable: false, includeInMobile: false },
      { id: 'status', numeric: false, label: 'Status', sortable: false, isSubHeading: true },
    ];

    const rows = !reductionActions ? [] : reductionActions.map(action => {
      const rowPeriodLocked =
        action.reportPeriodReductionAction.reportPeriod != null
          ? action.reportPeriodReductionAction.reportPeriod.locked === true
          : isReportPeriodLocked === true
      const annual = action.reportPeriodReductionAction.annualReduction
      const annualTonnes = annual != null ? Number(annual) / 1000 : null
      const isSuggested = !!action.reportPeriodReductionAction.recommendationEvidence
      const costPerTonne = costPerTonnePerYear({
        capexGbp: action.reportPeriodReductionAction.capexGbp,
        annualOpexDeltaGbp: action.reportPeriodReductionAction.annualOpexDeltaGbp,
        annualReductionKg: annual,
        lifetimeYears: action.reportPeriodReductionAction.reductionLifetimePeriods,
      })
      return {
        id: action.reportPeriodReductionAction.id,
        name: isSuggested ? (
          <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap>
            <span>{action.name}</span>
            <Chip label='Suggested' size='small' variant='outlined' title='Added from a recommendation based on your own figures. The saving is an estimate you can edit.' />
          </Stack>
        ) : action.name,
        costPerTonne: actionType === 'flag_removal' ? undefined : (costPerTonne != null ? formatCostPerTonne(costPerTonne).replace(' per tCO2e a year', '') : '—'),
        removalType: actionType === 'flag_removal'
          ? (action.removalType || '').split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || '—'
          : undefined,
        reportingPeriod:
          action.reportPeriodReductionAction.reportPeriod?.calendarYear ??
          action.reportPeriodReductionAction.reportPeriod?.friendlyName ??
          '—',
        projectedAnnualReduction:
          annualTonnes !== null && annualTonnes !== undefined ? (
            <NumericFormat value={annualTonnes} displayType="text" thousandSeparator="," decimalScale={2} suffix=" tCO2e" />
          ) : (
            '—'
          ),
        startDate: moment(action.reportPeriodReductionAction.createdAt).format('DD/MM/YYYY'),
        endDate: action.reportPeriodReductionAction.dueDate ? moment(action.reportPeriodReductionAction.dueDate).format('DD/MM/YYYY') : '',
        status: action.reportPeriodReductionAction.inProgress ? 'In Progress' : action.reportPeriodReductionAction.isCompleted ? 'Completed' : 'Not Started',
        actions: (
          <TableActionButtons
            id={action.reportPeriodReductionAction.id}
            includeEdit={!rowPeriodLocked}
            onEdit={() => editClickHandler(action.reportPeriodReductionAction.id)}
          />
        )
      }
    })

    const handleOrderChange = (orderBy: string, order: 'asc' | 'desc',) => {
      setOrderBy(orderBy)
      setOrder(order)
    };

    useEffect(() => {
      if (state && actionType !== 'flag_removal') {
        setDialogOpen(true)
      }
    }, [state, actionType])

    const editClickHandler = (selectedRowId: string) => {
      if (selectedRowId) {
        setDialogOpen(true)
        const basePath = actionType === 'flag_removal' ? '/reduction-plan/flag' : '/reduction-plan/actions';
        navigate(`${basePath}?&actionId=${selectedRowId}`)
      }
    }

    const closeDialogHandler = () => {
      setDialogOpen(false)
      navigate(actionType === 'flag_removal' ? '/reduction-plan/flag' : '/reduction-plan/actions')
    }

    const submitDialogHandler = (reportPeriodreductionActionId: string, values: any) => {
      const matched = reductionActions?.find((a) => a.reportPeriodReductionAction.id === reportPeriodreductionActionId);
      const periodLocked =
        matched?.reportPeriodReductionAction.reportPeriod != null
          ? matched.reportPeriodReductionAction.reportPeriod.locked === true
          : isReportPeriodLocked === true;
      if (periodLocked) {
        userNotificationsContext.openDialog({ active: true, title: 'Action Not Allowed', message: `The report period for this action is locked and cannot be modified`, buttonCopy: 'Ok', hideCancelButton: true })
      } else {
        if (!isDialogSubmitting) {
          setIsDialogSubmitting(true)
          reductionActionService.editReductionActionForReportPeriod(reportPeriodreductionActionId, values)
            .then(response => {
              getAllActionsForOrganisation();
              setDialogOpen(false)
              setIsDialogSubmitting(false)
            })
            .catch(err => {
              if (err.response.data.code === 1) {
                userNotificationsContext.openSnackbar({ open: true, message: 'Report period is locked', backgroundColor: 'text.primary' });
              } else {
                userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
              }
              setIsDialogSubmitting(false)
              console.log(err)
            })
        }
      }
    }

    const deleteDialogHandler = (reductionActionId: string, reportPeriodId: string,) => {
      reductionActionService.deleteReductionActionToReportPeriod(reductionActionId, reportPeriodId)
        .then(response => {
          if (inputPage > 0 && reductionActions && reductionActions.length === 1) {
            setInputPage(inputPage - 1)
          } else {
            getAllActionsForOrganisation();
          }
          userNotificationsContext.openSnackbar({ open: true, message: 'Deleted successfully' });
          setDialogOpen(false)
        })
        .catch(err => {
          if (err.response.data.code === 1) {
            userNotificationsContext.openSnackbar({ open: true, message: 'Report period is locked', backgroundColor: 'text.primary' });
          } else {
            userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
          }
          console.log(err)
          setDialogOpen(false)
        })
    }

    return (
      <Box>
        <TableDisplay
          onOrderChange={handleOrderChange}
          isMobileMode={isMobileMode}
          headCells={headCells}
          rows={rows}
          selected={selected}
          selectByIcon={true}
          setSelected={setSelected}
          inputPage={inputPage}
          setInputPage={setInputPage}
          totalRecords={totalRecords}
          perPage={perPage}
          setPerPage={setPerPage} />
        {openDialog && actionType !== 'flag_removal' &&
          <ReductionActionPlanDialog
            actionType={actionType}
            open={openDialog}
            isSubmitting={isDialogSubmitting}
            reductionActionsForOrganisation={reductionActions}
            getAllActionsForOrganisation={getAllActionsForOrganisation}
            closeHandler={closeDialogHandler}
            submitHandler={submitDialogHandler}
            deleteHandler={deleteDialogHandler} />}
      </Box>
    )
  }